/**
 * Utility functions for formatting and validating JSON content
 */

export interface JsonFormatResult {
	success: boolean;
	formatted?: string;
	error?: string;
}

/**
 * Attempts to format and validate JSON content
 * Handles various input formats including object-like syntax
 *
 * @param content - The content to format (JSON string, object-like syntax, etc.)
 * @returns JsonFormatResult with success status and formatted content or error message
 */
export function formatJsonContent(content: string): JsonFormatResult {
	try {
		// If content is empty, return early
		if (!content.trim()) {
			return {
				success: false,
				error: 'Content is empty',
			};
		}

		// Try to parse as JSON first
		let parsed;
		try {
			parsed = JSON.parse(content);
		} catch {
			// If it's not valid JSON, try to convert from object-like syntax
			// This handles cases like: { title: 'foo', body: 'bar' }
			try {
				// Replace unquoted property names with quoted ones
				const withQuotes = content.replace(/(\s*)(\w+)(\s*):(\s*)/g, '$1"$2"$3:$4');

				// Try to parse the modified content
				parsed = JSON.parse(withQuotes);
			} catch (secondError) {
				// If still can't parse, try to evaluate as a JavaScript object
				// This is safer than eval() and handles most common cases
				try {
					// Create a function that returns the object
					const func = new Function(`return ${content}`);
					parsed = func();

					// Validate that it's actually an object
					if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
						throw new Error('Not a valid object');
					}
				} catch (thirdError) {
					// If all else fails, return error
					return {
						success: false,
						error: "Unable to parse content. Please check your syntax and ensure it's a valid object.",
					};
				}
			}
		}

		// Format the parsed JSON with proper indentation
		const formatted = JSON.stringify(parsed, null, 2);

		return {
			success: true,
			formatted,
		};
	} catch (error) {
		console.error('Error formatting JSON:', error);
		return {
			success: false,
			error: 'Unexpected error while formatting JSON',
		};
	}
}

/**
 * Validates if a string is valid JSON
 *
 * @param content - The content to validate
 * @returns boolean indicating if the content is valid JSON
 */
export function isValidJson(content: string): boolean {
	try {
		JSON.parse(content);
		return true;
	} catch {
		return false;
	}
}

/**
 * Attempts to convert object-like syntax to valid JSON
 *
 * @param content - The content to convert (e.g., { title: 'foo', body: 'bar' })
 * @returns JsonFormatResult with success status and converted content or error message
 */
export function convertObjectLikeToJson(content: string): JsonFormatResult {
	try {
		// Replace unquoted property names with quoted ones
		const withQuotes = content.replace(/(\s*)(\w+)(\s*):(\s*)/g, '$1"$2"$3:$4');

		// Try to parse the modified content
		const parsed = JSON.parse(withQuotes);

		// Format the parsed JSON with proper indentation
		const formatted = JSON.stringify(parsed, null, 2);

		return {
			success: true,
			formatted,
		};
	} catch (error) {
		return {
			success: false,
			error: 'Unable to convert object-like syntax to JSON',
		};
	}
}

/**
 * Minifies JSON content by removing unnecessary whitespace
 *
 * @param content - The JSON content to minify
 * @returns JsonFormatResult with success status and minified content or error message
 */
export function minifyJson(content: string): JsonFormatResult {
	try {
		const parsed = JSON.parse(content);
		const minified = JSON.stringify(parsed);

		return {
			success: true,
			formatted: minified,
		};
	} catch (error) {
		return {
			success: false,
			error: 'Invalid JSON content cannot be minified',
		};
	}
}
